import React, { useCallback, useEffect, useRef, useState } from "react";
import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import toast from "react-hot-toast";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

type Pagarme3dsResponseType = {
  authenticated_card: string;
  challenge_canceled: boolean;
  tds_server_trans_id: string;
  trans_status: string;
};

type Pagarme3dsProviderContextData = {
  loaded: boolean;
  initChallenge: (
    token: string,
    orderPayload: any,
  ) => Promise<Pagarme3dsResponseType>;
};

const Pagarme3dsProviderContext = createContext<Pagarme3dsProviderContextData>(
  {} as Pagarme3dsProviderContextData,
);

function Pagarme3dsProvider({ children }: PropsWithChildren) {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [challengeLoaded, setChallengeLoaded] = useState(false);

  const challengeRef = useRef<HTMLDivElement>(null);
  const tdsMethodRef = useRef<HTMLDivElement>(null);

  const pendingRequest = useRef<{
    token: string;
    orderPayload: any;
    resolve: (value: Pagarme3dsResponseType) => void;
    reject: (reason?: any) => void;
  } | null>(null);

  const is3dsScriptLoaded = () =>
    !!document.querySelector(
      `script[src="${process.env.GATSBY_PAGARME_3DS_SCRIPT_URL}"]`,
    );

  useEffect(() => {
    if (is3dsScriptLoaded()) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");

    script.src = process.env.GATSBY_PAGARME_3DS_SCRIPT_URL!;
    script.async = false;
    script.defer = true;

    script.onload = () => setScriptLoaded(true);

    script.onerror = () =>
      toast.error("Não foi possível carregar o serviço de autenticação.");

    document.head.appendChild(script);
  }, []);

  const runChallenge = useCallback(async () => {
    if (
      !pendingRequest.current ||
      !challengeRef.current ||
      !tdsMethodRef.current
    ) {
      return;
    }

    const { token, orderPayload, resolve, reject } = pendingRequest.current;

    setChallengeLoaded(false);

    try {
      const iframeLoaded = new Promise<void>((resolveIframe) => {
        const existingIframe =
          challengeRef.current!.querySelector<HTMLIFrameElement>("iframe");

        if (existingIframe) {
          resolveIframe();
          return;
        }

        const observer = new MutationObserver(() => {
          const iframe =
            challengeRef.current?.querySelector<HTMLIFrameElement>("iframe");

          if (!iframe) {
            return;
          }

          observer.disconnect();

          iframe.addEventListener("load", () => resolveIframe(), {
            once: true,
          });

          try {
            if (
              iframe.contentDocument?.readyState === "complete" ||
              iframe.contentWindow?.document.readyState === "complete"
            ) {
              resolveIframe();
            }
          } catch {
            resolveIframe();
          }
        });

        observer.observe(challengeRef.current!, {
          childList: true,
          subtree: true,
        });
      });

      const challengePromise = (window as any).TDS.init(
        {
          token,
          tds_method_container_element: tdsMethodRef.current,
          challenge_container_element: challengeRef.current,
          use_default_challenge_iframe_style: true,
          challenge_window_size: "03",
        },
        orderPayload,
      );

      await iframeLoaded;

      setChallengeLoaded(true);

      const response: Pagarme3dsResponseType[] = await challengePromise;

      resolve(response[0]);
    } catch (error) {
      if (error instanceof (window as any).TDS.TDSError) {
        console.error("Erro 3DS:", {
          message: error.message,
          code: error.getCode(),
          status: error.getStatus(),
          requestID: error.getRequestID(),
          authenticationID: error.getAuthenticationID(),
          details: error.getDetails(),
          documentationURL: error.getDocumentationURL(),
        });

        switch (error.getCode()) {
          case "UNAUTHORIZED":
          case "BIN_FRAUD_SUSPECT":
            toast.error(
              "Este cartão não pode ser utilizado. Por favor, utilize outro cartão.",
            );
            break;
          case "SCHEME_NOT_SUPPORTED":
            toast.error(
              "Esta bandeira de cartão não é suportada. Aceitamos Mastercard, Visa e Amex.",
            );
            break;
          case "INTERNAL_SERVER_ERROR":
          case "SERVICE_UNAVAILABLE":
            toast.error(
              "Erro temporário. Por favor, tente novamente em alguns instantes.",
            );
            break;
          default:
            toast.error(
              "Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.",
            );
        }
      } else {
        console.error(error);

        toast.error(
          "Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.",
        );
      }

      reject(error);
    } finally {
      pendingRequest.current = null;
      setDialogOpen(false);
      setChallengeLoaded(false);
    }
  }, []);

  const onCancelButtonClick = useCallback(() => {
    if (!pendingRequest.current) {
      return;
    }

    pendingRequest.current.reject(new Error("Challenge canceled by user"));
    pendingRequest.current = null;

    setDialogOpen(false);
    setChallengeLoaded(false);
  }, []);

  const initChallenge = useCallback(
    (token: string, orderPayload: any) =>
      new Promise<Pagarme3dsResponseType>((resolve, reject) => {
        pendingRequest.current = {
          token,
          orderPayload,
          resolve,
          reject,
        };

        setDialogOpen(true);
      }),
    [],
  );

  const value = useMemo<Pagarme3dsProviderContextData>(
    () => ({
      loaded: scriptLoaded,
      initChallenge,
    }),
    [scriptLoaded, initChallenge],
  );

  return (
    <Pagarme3dsProviderContext.Provider value={value}>
      {children}

      <Dialog
        open={dialogOpen}
        maxWidth="lg"
        fullWidth
        onClose={() => setDialogOpen(false)}
        TransitionProps={{
          onEntered: runChallenge,
        }}
      >
        <DialogTitle>Verificação de segurança</DialogTitle>

        <DialogContent dividers>
          <div className="relative">
            <div
              ref={tdsMethodRef}
              id="tdsMethodContainer"
              style={{ display: "none" }}
            />

            <div ref={challengeRef} id="challengeContainer" />

            {!challengeLoaded && (
              <div className="flex items-center justify-center p-10">
                <CircularProgress />
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions sx={{ bgcolor: "#eee" }}>
          <Button
            color="error"
            startIcon={<CloseIcon />}
            onClick={onCancelButtonClick}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Pagarme3dsProviderContext.Provider>
  );
}

export const usePagarme3ds = () => useContext(Pagarme3dsProviderContext);

export default Pagarme3dsProvider;
